/**
 * Admin Disputes & Verification Management
 * - View and resolve customer disputes
 * - View blocked drivers and verification reports
 * - Unblock drivers with audit trail
 */
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  AlertTriangle, Shield, UserX, Scale, Clock, DollarSign, 
  CheckCircle, XCircle, Eye, MessageSquare, Unlock, Car
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DisputesManagement = () => {
  const [disputes, setDisputes] = useState([]);
  const [blockedDrivers, setBlockedDrivers] = useState([]);
  const [verificationReports, setVerificationReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [resolveModal, setResolveModal] = useState(false);
  const [unblockModal, setUnblockModal] = useState(false);
  const [resolution, setResolution] = useState({
    resolution: "favor_customer",
    customer_refund_percentage: 100,
    admin_notes: ""
  });
  const [unblockNotes, setUnblockNotes] = useState("");
  const [processing, setProcessing] = useState(false);

  const fetchData = async () => {
    const token = localStorage.getItem("token");
    try {
      const [disputesRes, blockedRes, reportsRes] = await Promise.all([
        fetch(`${API_URL}/api/disputes/admin/pending`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/verification/admin/blocked-drivers`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/verification/admin/reports`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (disputesRes.ok) {
        const data = await disputesRes.json();
        setDisputes(data.disputes || []);
      }
      if (blockedRes.ok) {
        const data = await blockedRes.json();
        setBlockedDrivers(data.blocked_drivers || []);
      }
      if (reportsRes.ok) {
        const data = await reportsRes.json();
        setVerificationReports(data.reports || []);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load disputes data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleResolveDispute = async () => {
    if (!selectedDispute || !resolution.admin_notes) {
      toast.error("Please provide admin notes");
      return;
    }

    setProcessing(true);
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API_URL}/api/disputes/admin/resolve/${selectedDispute.id}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(resolution)
      });

      const data = await res.json();
      if (data.success) {
        toast.success(`Dispute resolved! Customer refund: J$${data.customer_refund?.toFixed(2) || 0}`);
        setResolveModal(false);
        setSelectedDispute(null);
        fetchData();
      } else {
        toast.error(data.error?.message || "Failed to resolve dispute");
      }
    } catch (error) {
      toast.error("Failed to resolve dispute");
    } finally {
      setProcessing(false);
    }
  };

  const handleUnblockDriver = async () => {
    if (!selectedDriver || !unblockNotes) {
      toast.error("Please provide admin notes for audit");
      return;
    }

    setProcessing(true);
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API_URL}/api/verification/admin/unblock/${selectedDriver.id}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ admin_notes: unblockNotes })
      });

      const data = await res.json();
      if (data.success) {
        toast.success("Driver unblocked successfully");
        setUnblockModal(false);
        setSelectedDriver(null);
        setUnblockNotes("");
        fetchData();
      } else {
        toast.error(data.error?.message || "Failed to unblock driver");
      }
    } catch (error) {
      toast.error("Failed to unblock driver");
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleString();
  };

  const formatCurrency = (amount) => {
    return `J$${(amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
  };

  return (
    <div className="min-h-screen bg-slate-900 p-6" data-testid="disputes-management">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Scale className="h-8 w-8 text-amber-500" />
              Disputes & Verification
            </h1>
            <p className="text-gray-400 mt-1">Manage customer disputes and driver verification issues</p>
          </div>
          <Button onClick={fetchData} variant="outline" className="border-slate-600">
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-red-500/20 border-red-500/30">
            <CardContent className="p-4 flex items-center gap-4">
              <AlertTriangle className="h-10 w-10 text-red-400" />
              <div>
                <p className="text-2xl font-bold text-white">{disputes.length}</p>
                <p className="text-sm text-red-200">Open Disputes</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-orange-500/20 border-orange-500/30">
            <CardContent className="p-4 flex items-center gap-4">
              <UserX className="h-10 w-10 text-orange-400" />
              <div>
                <p className="text-2xl font-bold text-white">{blockedDrivers.length}</p>
                <p className="text-sm text-orange-200">Blocked Drivers</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-purple-500/20 border-purple-500/30">
            <CardContent className="p-4 flex items-center gap-4">
              <Shield className="h-10 w-10 text-purple-400" />
              <div>
                <p className="text-2xl font-bold text-white">{verificationReports.filter(r => r.status === "pending_review").length}</p>
                <p className="text-sm text-purple-200">Pending Reports</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-emerald-500/20 border-emerald-500/30">
            <CardContent className="p-4 flex items-center gap-4">
              <CheckCircle className="h-10 w-10 text-emerald-400" />
              <div>
                <p className="text-2xl font-bold text-white">{verificationReports.filter(r => r.status === "resolved").length}</p>
                <p className="text-sm text-emerald-200">Resolved</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="disputes" className="space-y-4">
          <TabsList className="bg-slate-800 border-slate-700">
            <TabsTrigger value="disputes" className="data-[state=active]:bg-amber-500">
              Disputes ({disputes.length})
            </TabsTrigger>
            <TabsTrigger value="blocked" className="data-[state=active]:bg-amber-500">
              Blocked Drivers ({blockedDrivers.length})
            </TabsTrigger>
            <TabsTrigger value="reports" className="data-[state=active]:bg-amber-500">
              Verification Reports ({verificationReports.length})
            </TabsTrigger>
          </TabsList>

          {/* Disputes Tab */}
          <TabsContent value="disputes">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Open Customer Disputes</CardTitle>
              </CardHeader>
              <CardContent>
                {disputes.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No open disputes</p>
                ) : (
                  <div className="space-y-4">
                    {disputes.map((dispute) => (
                      <div 
                        key={dispute.id} 
                        className="bg-slate-700/50 rounded-lg p-4 space-y-3"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <Badge variant="destructive">{dispute.entity_type?.toUpperCase()}</Badge>
                              <span className="text-white font-medium">{dispute.id}</span>
                            </div>
                            <p className="text-sm text-gray-400 mt-1">
                              Entity: {dispute.entity_id}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold text-red-400">
                              {formatCurrency(dispute.amount_in_dispute)}
                            </p>
                            <Badge variant="outline" className="text-amber-400 border-amber-400">
                              {dispute.funds_on_hold ? "FUNDS ON HOLD" : "PENDING"}
                            </Badge>
                          </div>
                        </div>

                        <div className="bg-slate-800 rounded p-3">
                          <p className="text-sm text-gray-300">{dispute.reason}</p>
                        </div>

                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-4 text-gray-400">
                            <span>Customer: {dispute.customer_name}</span>
                            <span>|</span>
                            <span>Driver: {dispute.driver_id}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-400">
                            <Clock className="h-4 w-4" />
                            {formatDate(dispute.created_at)}
                          </div>
                        </div>

                        <Button
                          onClick={() => {
                            setSelectedDispute(dispute);
                            setResolution({
                              resolution: "favor_customer",
                              customer_refund_percentage: 100,
                              admin_notes: ""
                            });
                            setResolveModal(true);
                          }}
                          className="w-full bg-amber-500 hover:bg-amber-600 text-black"
                        >
                          <Scale className="h-4 w-4 mr-2" />
                          Resolve Dispute
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Blocked Drivers Tab */}
          <TabsContent value="blocked">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Blocked Drivers</CardTitle>
              </CardHeader>
              <CardContent>
                {blockedDrivers.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No blocked drivers</p>
                ) : (
                  <div className="space-y-4">
                    {blockedDrivers.map((driver) => (
                      <div 
                        key={driver.id} 
                        className="bg-slate-700/50 rounded-lg p-4 space-y-3"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-white font-medium">
                              {driver.user_info?.name || "Unknown Driver"}
                            </p>
                            <p className="text-sm text-gray-400">{driver.user_info?.email}</p>
                            <p className="text-sm text-gray-400">{driver.user_info?.phone}</p>
                          </div>
                          <Badge variant="destructive">BLOCKED</Badge>
                        </div>

                        <div className="bg-red-500/10 rounded p-3 border border-red-500/30">
                          <p className="text-sm text-red-300">
                            <strong>Reason:</strong> {driver.block_reason}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            Blocked: {formatDate(driver.blocked_at)}
                          </p>
                        </div>

                        {driver.report?.appeal_submitted && (
                          <div className="bg-amber-500/10 rounded p-3 border border-amber-500/30">
                            <p className="text-sm text-amber-300">
                              <strong>Appeal:</strong> {driver.report?.appeal_reason}
                            </p>
                          </div>
                        )}

                        <Button
                          onClick={() => {
                            setSelectedDriver(driver);
                            setUnblockNotes("");
                            setUnblockModal(true);
                          }}
                          className="w-full bg-emerald-500 hover:bg-emerald-600"
                        >
                          <Unlock className="h-4 w-4 mr-2" />
                          Unblock Driver
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Verification Reports Tab */}
          <TabsContent value="reports">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Verification Mismatch Reports</CardTitle>
              </CardHeader>
              <CardContent>
                {verificationReports.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No verification reports</p>
                ) : (
                  <div className="space-y-4">
                    {verificationReports.map((report) => (
                      <div 
                        key={report.id} 
                        className="bg-slate-700/50 rounded-lg p-4 space-y-3"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <Car className="h-5 w-5 text-purple-400" />
                              <span className="text-white font-medium">{report.id}</span>
                            </div>
                            <p className="text-sm text-gray-400 mt-1">
                              Ride: {report.ride_id}
                            </p>
                          </div>
                          <Badge 
                            variant={report.status === "resolved" ? "success" : "warning"}
                            className={report.status === "resolved" ? "bg-emerald-500" : "bg-orange-500"}
                          >
                            {report.status?.toUpperCase().replace("_", " ")}
                          </Badge>
                        </div>

                        <div className="bg-slate-800 rounded p-3">
                          <p className="text-sm text-gray-300">
                            <strong>Type:</strong> {report.mismatch_type?.replace("_", " ")}
                          </p>
                          <p className="text-sm text-gray-300 mt-1">{report.description}</p>
                        </div>

                        <div className="flex items-center justify-between text-sm text-gray-400">
                          <span>Reported by: {report.passenger_name}</span>
                          <span>{formatDate(report.created_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Resolve Dispute Modal */}
      <Dialog open={resolveModal} onOpenChange={setResolveModal}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Resolve Dispute</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-700/50 rounded p-3">
              <p className="text-sm text-gray-300">
                Amount in dispute: <strong className="text-white">{formatCurrency(selectedDispute?.amount_in_dispute)}</strong>
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Resolution</Label>
              <Select
                value={resolution.resolution}
                onValueChange={(v) => setResolution({
                  ...resolution, 
                  resolution: v,
                  customer_refund_percentage: v === "favor_customer" ? 100 : v === "favor_driver" ? 0 : 50
                })}
              >
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="favor_customer">Favor Customer (Full Refund)</SelectItem>
                  <SelectItem value="favor_driver">Favor Driver (No Refund)</SelectItem>
                  <SelectItem value="split">Split (Partial Refund)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {resolution.resolution === "split" && (
              <div className="space-y-2">
                <Label className="text-white">Customer Refund %</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={resolution.customer_refund_percentage}
                  onChange={(e) => setResolution({...resolution, customer_refund_percentage: parseInt(e.target.value)})}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-white">Admin Notes (Required)</Label>
              <Textarea
                placeholder="Explain your decision..."
                value={resolution.admin_notes}
                onChange={(e) => setResolution({...resolution, admin_notes: e.target.value})}
                className="bg-slate-700 border-slate-600 text-white min-h-24"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveModal(false)} className="border-slate-600">
              Cancel
            </Button>
            <Button 
              onClick={handleResolveDispute} 
              className="bg-amber-500 hover:bg-amber-600 text-black"
              disabled={processing || !resolution.admin_notes}
            >
              {processing ? "Processing..." : "Resolve"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unblock Driver Modal */}
      <Dialog open={unblockModal} onOpenChange={setUnblockModal}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Unblock Driver</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-700/50 rounded p-3">
              <p className="text-sm text-gray-300">
                Driver: <strong className="text-white">{selectedDriver?.user_info?.name}</strong>
              </p>
              <p className="text-sm text-gray-300">
                Email: {selectedDriver?.user_info?.email}
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Admin Notes (For Audit)</Label>
              <Textarea
                placeholder="Explain why you are unblocking this driver..."
                value={unblockNotes}
                onChange={(e) => setUnblockNotes(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white min-h-24"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnblockModal(false)} className="border-slate-600">
              Cancel
            </Button>
            <Button 
              onClick={handleUnblockDriver} 
              className="bg-emerald-500 hover:bg-emerald-600"
              disabled={processing || !unblockNotes}
            >
              {processing ? "Processing..." : "Unblock Driver"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DisputesManagement;
